import { parseSessionPlanDeclaration } from '../../domain/constants/holdTaskTypes';
import { completeDelivery, isSameDelivery } from '../../domain/services/sessionPlanDeliveryFix';

/**
 * Applies one service delivery to sessions held for a missing service or place: one picked from
 * billing items, or every one a plan ran.
 *
 * ONE SESSION FAILING DOES NOT STOP THE OTHERS. Each write is its own, and the result names the ones
 * that failed so they can be retried.
 *
 * @param {{ sessionDelivery: import('../ports/SessionDeliverySurfaces.port').SessionDeliverySurfaces }} deps
 * @returns {(input: {
 *   sessionActivityIds: string[],
 *   chosen: import('../../domain/services/sessionPlanDeliveryFix').Delivery,
 * }) => Promise<{fixed: number, failed: string[]}>}
 */
export function createFixSessionDeliveries({ sessionDelivery }) {
  return async ({ sessionActivityIds, chosen }) => {
    const sessions = await sessionDelivery.readSessionDeliveries(sessionActivityIds);
    const writes = sessions
      .map(({ id, delivery }) => ({ id, current: delivery, next: completeDelivery(delivery, chosen) }))
      .filter(({ current, next }) => !isSameDelivery(current, next));
    const results = await Promise.allSettled(writes.map(({ id, next }) => (
      sessionDelivery.writeSessionDelivery(id, next)
    )));
    const failed = writes.filter((write, index) => results[index].status === 'rejected').map(({ id }) => id);

    return { fixed: writes.length - failed.length, failed };
  };
} // createFixSessionDeliveries()

/**
 * Every session a plan ran that is held for a missing service or place, and, when the user chooses,
 * the plan itself.
 *
 * THE SESSIONS FIRST, THEN THE PLAN. The sessions are what the held items bill from, so they are the
 * fix; the plan only stops the next session holding. A plan write that fails leaves the sessions
 * fixed and says so, rather than undoing work that succeeded.
 *
 * @param {{
 *   heldSessions: import('../ports/SessionDeliverySurfaces.port').HeldSessionRepository,
 *   sessionDelivery: import('../ports/SessionDeliverySurfaces.port').SessionDeliverySurfaces,
 * }} deps
 * ONLY THE SESSIONS HELD FOR THE TASK'S OWN REASON. A plan can hold some sessions for a missing
 * service and others for a missing place; each is its own task, and fixing one must not reach into
 * the other's sessions.
 *
 * @returns {(input: {
 *   clinicId: string,
 *   sessionPlanDeclaration: string,
 *   holdReason: string,
 *   chosen: import('../../domain/services/sessionPlanDeliveryFix').Delivery,
 *   alsoOnPlan: boolean,
 * }) => Promise<{fixed: number, failed: string[], plan: 'updated'|'failed'|'untouched'}>}
 */
export function createFixSessionPlanDelivery({ heldSessions, sessionDelivery }) {
  const fixSessionDeliveries = createFixSessionDeliveries({ sessionDelivery });

  return async ({
    clinicId, sessionPlanDeclaration, holdReason, chosen, alsoOnPlan,
  }) => {
    const declaration = parseSessionPlanDeclaration(sessionPlanDeclaration);

    if (!declaration) {
      throw new TypeError(`Not a session plan declaration: ${sessionPlanDeclaration}`);
    }

    const held = await heldSessions.findHeldSessions(clinicId, sessionPlanDeclaration, holdReason);
    const { fixed, failed } = await fixSessionDeliveries({
      sessionActivityIds: [...new Set(held.map((item) => item.sessionActivityId))],
      chosen,
    });

    let plan = 'untouched';

    if (alsoOnPlan) {
      try {
        await sessionDelivery.writePlanDeclaration(declaration.sessionPlanId, declaration.activityType, chosen);
        plan = 'updated';
      } catch (error) {
        console.error('Failed to set the service delivery on a session plan', error);
        plan = 'failed';
      }
    }

    return { fixed, failed, plan };
  };
} // createFixSessionPlanDelivery()
